
import React, { useState, useContext } from "react";

import { useAuth } from "../context/auth";
import { Redirect } from "react-router-dom";
import { store } from "../context/store.js";

import {
    CHANGE_LOGGED_IN,
    CHANGE_PERIOD_REPORT_ID,
    CHANGE_QUERY_CONTEXT,
    CHANGE_CLIENT
} from "../constants/action-types";

import { functions } from "../constants/common-functions";

import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faEye,
    faEyeSlash,
    faArrowRight
} from "@fortawesome/free-solid-svg-icons";

import omniscanLogo from "../../images/omniscanlogo.png";

import BlurredBackdropModal from "../components/BlurredBackdropModal";
import ChangePasswordCard from "../components/ChangePasswordCard";


//========================================================
// OmniScan Login
//========================================================

const ACCENT = "#39b9dc";

const styles = {

    page: {
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        background: "transparent",
        boxSizing: "border-box",
        padding: "24px"
    },


	card: {
	    position: "relative",
	    zIndex: 2,

	    width: "100%",
	    maxWidth: "420px",

	    padding: "48px 38px 38px",
	    boxSizing: "border-box",

	    // Bright, clean white glass
	    background: "rgba(255, 255, 255, 0.96)",

	    // Very subtle frosted effect
	    backdropFilter: "blur(12px)",
	    WebkitBackdropFilter: "blur(12px)",

	    borderRadius: "18px",

	    border: "1px solid rgba(255,255,255,0.95)",

	    // Soft floating shadow
	    boxShadow:
	        "0 25px 75px rgba(20,40,55,0.16), " +
	        "0 4px 18px rgba(20,40,55,0.06)",

	    textAlign: "center"
	},


    logo: {
        display: "block",
        width: "225px",
        maxWidth: "100%",
        height: "auto",
        margin: "0 auto"
    },

    accentLine: {
        width: "56px",
        height: "3px",
        margin: "24px auto 30px",
        borderRadius: "3px",
        background:
            "linear-gradient(90deg, #37c9d3, #40aee8)",
        boxShadow:
            "0 0 12px rgba(57,185,220,0.25)"
    },

    eyebrow: {
        color: "#657783",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "2.5px",
        textTransform: "uppercase",
        marginBottom: "30px"
    },

    field: {
        marginBottom: "22px"
    },

    input: {
        fontSize: "15px",
        color: "#172b36",
        fontWeight: 400
    },

    button: {
        height: "50px",
        marginTop: "12px",

        background:
            "linear-gradient(135deg, #163747, #102632)",

        color: "#ffffff",

        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "2px",

        borderRadius: "6px",

        border:
            "1px solid rgba(57,185,220,0.45)",

        boxShadow:
            "0 8px 22px rgba(16,45,60,0.22)",

        textTransform: "uppercase"
    },

    buttonContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "14px",
        width: "100%"
    },

    error: {
        marginTop: "18px",
        padding: "11px 14px",
        borderRadius: "6px",
        background: "rgba(211,47,47,0.07)",
        border: "1px solid rgba(211,47,47,0.18)",
        color: "#b32626",
        fontSize: "13px"
    },

    footer: {
        marginTop: "32px",
        paddingTop: "18px",
        borderTop:
            "1px solid rgba(70,110,130,0.12)",
        color: "#82939d",
        fontSize: "10px",
        letterSpacing: "1.7px",
        textTransform: "uppercase"
    }

};


//========================================================
// Component
//========================================================

export default function SystemLogin() {

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [loggedIn, setLoggedIn] = useState(false);

    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");

    const [invalidCredentials, setInvalidCredentials] =
        useState(false);

    const [loading, setLoading] = useState(false);

    const [textFieldsDisabled, setTextFieldsDisabled] =
        useState(false);

    const [showChangePassword, setShowChangePassword] =
        useState(false);

    const [showPassword, setShowPassword] =
        useState(false);


    //----------------------------------------------------
    // Context
    //----------------------------------------------------

    const { setAuthTokens } = useAuth();

    const globalState = useContext(store);

    if (!globalState) {
        return null;
    }

    const { dispatch } = globalState;


    //----------------------------------------------------
    // Set User
    //----------------------------------------------------

    const setUser = (user) => {

        dispatch({
            type: CHANGE_LOGGED_IN,
            value: user
        });

    };


    //----------------------------------------------------
    // Set Query Context
    //----------------------------------------------------

    const setQueryContext = () => {

        functions.getObjectFromAjax(
            "/resolutionsoftwareplatform/utilisation/latestutilisationsurveyid",
            null,
            function (surveyId) {

                dispatch({
                    type: CHANGE_QUERY_CONTEXT,
                    value: {
                        utilisationSurveyId: surveyId
                    }
                });

                setLoggedIn(true);

            }
        );

    };


    //----------------------------------------------------
    // Login
    //----------------------------------------------------

    function postLogin() {

        if (loading || textFieldsDisabled) {
            return;
        }

        setInvalidCredentials(false);
        setLoading(true);
        setTextFieldsDisabled(true);

        const user = {
            username: userName,
            password: password
        };

        fetch("/resolutionsoftwareplatform/login", {

            credentials: "include",
            method: "POST",
            body: JSON.stringify(user)

        })
        .then(result => {

            if (result.status === 200) {

                let jwtToken =
                    result.headers.get("Authorization");

                setAuthTokens(jwtToken);

                //------------------------------------------------
                // Get User
                //------------------------------------------------

                functions.getUser(function (user) {

                    setUser(user);

                    if (user.passwordResetRequired) {

                        setShowChangePassword(true);
                        setLoading(false);
                        setTextFieldsDisabled(false);

                        return;
                    }

                    //------------------------------------------------
                    // Get Client
                    //------------------------------------------------

                    functions.getArrayFromAjax(
                        "/resolutionsoftwareplatform/getclient",
                        null,
                        function (client) {

                            dispatch({
                                type: CHANGE_CLIENT,
                                value: client
                            });

                            dispatch({
                                type: CHANGE_PERIOD_REPORT_ID,
                                value: 95
                            });

                            setQueryContext();

                            setLoggedIn(true);

                        }
                    );

                });

            } else {

                setInvalidCredentials(true);
                setLoading(false);
                setTextFieldsDisabled(false);

            }

        })
        .catch(function () {

            setInvalidCredentials(true);
            setLoading(false);
            setTextFieldsDisabled(false);

        });

    }


    //----------------------------------------------------
    // Enter Key
    //----------------------------------------------------

    const onKeyDown = (event) => {

        if (event.key === "Enter") {

            event.preventDefault();

            postLogin();

        }

    };


    //----------------------------------------------------
    // Redirect
    //----------------------------------------------------

    if (loggedIn) {
        return <Redirect to="/home" />;
    }


    //----------------------------------------------------
    // Shared Input Properties
    //----------------------------------------------------

    const inputProps = {

        style: styles.input,

        disableUnderline: false

    };


    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div style={styles.page}>

            {/*===========================================
              Login Card
            ===========================================*/}

            <div style={styles.card}>

                {/*---------------------------------------
                  Logo
                ---------------------------------------*/}

                <img
                    src={omniscanLogo}
                    alt="OmniScan Pro"
                    style={styles.logo}
                />

                <div style={styles.accentLine} />

                <div style={styles.eyebrow}>
                    Property Intelligence Hub
                </div>


                {/*---------------------------------------
                  Username
                ---------------------------------------*/}

                <TextField
                    fullWidth
                    variant="standard"
                    placeholder="Username"
                    autoComplete="username"
                    value={userName}
                    disabled={textFieldsDisabled}
                    onChange={(event) => {

                        setUserName(event.target.value);
                        setInvalidCredentials(false);

                    }}
                    onKeyDown={onKeyDown}
                    style={styles.field}
                    InputProps={inputProps}
                />


                {/*---------------------------------------
                  Password
                ---------------------------------------*/}

                <TextField
                    fullWidth
                    variant="standard"
                    type={
                        showPassword
                            ? "text"
                            : "password"
                    }
                    placeholder="Password"
                    autoComplete="current-password"
                    value={password}
                    disabled={textFieldsDisabled}
                    onChange={(event) => {

                        setPassword(event.target.value);
                        setInvalidCredentials(false);

                    }}
                    onKeyDown={onKeyDown}
                    style={{
                        ...styles.field,
                        marginBottom: "24px"
                    }}
                    InputProps={{

                        ...inputProps,

                        endAdornment: (

                            <InputAdornment position="end">

                                <IconButton
                                    size="small"
                                    edge="end"
                                    aria-label={
                                        showPassword
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                    onClick={() =>
                                        setShowPassword(
                                            !showPassword
                                        )
                                    }
                                    disabled={textFieldsDisabled}
                                    style={{
                                        color: "#617580"
                                    }}
                                >

                                    <FontAwesomeIcon
                                        icon={
                                            showPassword
                                                ? faEyeSlash
                                                : faEye
                                        }
                                        style={{
                                            fontSize: "15px"
                                        }}
                                    />

                                </IconButton>

                            </InputAdornment>

                        )

                    }}
                />


                {/*---------------------------------------
                  Login Button
                ---------------------------------------*/}

                <Button
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    onClick={postLogin}
                    style={{
                        ...styles.button,
                        opacity: loading ? 0.7 : 1
                    }}
                >

                    {loading ? (

                        <CircularProgress
                            size={23}
                            style={{
                                color: ACCENT
                            }}
                        />

                    ) : (

                        <span style={styles.buttonContent}>

                            LOGIN

                            <FontAwesomeIcon
                                icon={faArrowRight}
                                style={{
                                    color: ACCENT,
                                    fontSize: "13px"
                                }}
                            />

                        </span>

                    )}

                </Button>


                {/*---------------------------------------
                  Invalid Credentials
                ---------------------------------------*/}

                {invalidCredentials && (

                    <div
                        role="alert"
                        style={styles.error}
                    >
                        Invalid username or password
                    </div>

                )}


                {/*---------------------------------------
                  Footer
                ---------------------------------------*/}

                <div style={styles.footer}>
                    Secure Estate Access
                </div>

            </div>


            {/*===========================================
              Change Password Modal
            ===========================================*/}

            <BlurredBackdropModal
                show={showChangePassword}
                onClose={() =>
                    setShowChangePassword(false)
                }
            >

                <ChangePasswordCard
                    onClose={() => {

                        setShowChangePassword(false);
                        setLoggedIn(true);

                    }}
                />

            </BlurredBackdropModal>


            {/*===========================================
              Accent Styling
            ===========================================*/}

            <style>
                {`

                    .MuiInput-underline:after {
                        border-bottom-color: ${ACCENT} !important;
                    }

                    .MuiInput-underline:hover:not(.Mui-disabled):before {
                        border-bottom-color: #70bbd1 !important;
                    }

                    @media (max-width: 480px) {

                        .MuiInputBase-input {
                            font-size: 16px !important;
                        }

                    }

                `}
            </style>

        </div>

    );

}
